import type { BankItem } from "../../engine/bank-types";

/**
 * 原文理解 — does the student actually know what the sentence says?
 *
 * Word-level and clause-level readings where the common modern guess is
 * wrong. Every distractor is a reading someone really holds — often one
 * 楊伯峻 names and rejects in his notes — never filler.
 *
 * Option texts are kept to a similar length on purpose: if the right answer
 * is always the longest one, the item can be passed without reading it.
 */
export const COMPREHENSION_ITEMS: BankItem[] = [
  {
    id: "cm-1-1a",
    type: "comprehension",
    refs: [1],
    concepts: ["xue"],
    difficulty: 1,
    cognitive: "understand",
    purpose: "分辨「習」是實習演練而非溫習記誦",
    stem: "「學而時習之」的「習」，楊伯峻依周秦古書的用例取哪個意思？",
    context: "子曰：“学而时习之，不亦说乎？”",
    options: [
      { id: "a", text: "在適當的時候實習、演練所學" },
      { id: "b", text: "時常溫習、反覆記誦所學", why: "朱熹《集注》的講法。楊伯峻指出「時常」是用後代詞義解古書。" },
      { id: "c", text: "依季節安排讀書的進度", why: "把「時」讀成節令，古書無此用例，也與下文的喜悅無關。" },
      { id: "d", text: "習慣於學習這件事本身", why: "「習」帶賓語「之」，指演練所學，不是養成習慣。" },
    ],
    answerId: "a",
    explanation: "禮、樂、射、御這些功課非演習不可，所以「習」講作實習最合原意；學了能上手，喜悅才有著落。",
  },
  {
    id: "cm-1-1b",
    type: "comprehension",
    refs: [1],
    concepts: ["xue", "junzi"],
    difficulty: 2,
    cognitive: "analyze",
    purpose: "看出三句由己及人再收回自身的遞進",
    stem: "「人不知，而不慍」放在這一章末尾，作用是什麼？",
    options: [
      { id: "a", text: "把學的價值收回到自身，不待人知" },
      { id: "b", text: "抱怨當時無人理解自己的學問", why: "全章語氣是喜悅與自持，「不慍」正是不抱怨。" },
      { id: "c", text: "說明學問要藏起來不讓人知道", why: "「有朋自遠方來」正是樂於交流，並不主張藏學。" },
      { id: "d", text: "指出不被理解是因為學得不夠", why: "那是「患其不能也」的意思，此處講的是不被理解時的態度。" },
    ],
    answerId: "a",
    explanation: "三句從自修之悅、同道之樂，收到不待人知而自安，正是全書開篇立下的君子基調。",
  },
  {
    id: "cm-1-13",
    type: "comprehension",
    refs: [13],
    concepts: ["xin", "li"],
    difficulty: 3,
    cognitive: "analyze",
    purpose: "看出承諾須先合義才談得上守信",
    stem: "「信近於義，言可復也」——這句話對「守信」加了什麼前提？",
    context: "有子曰：“信近于义，言可复也。恭近于礼，远耻辱也。”",
    options: [
      { id: "a", text: "所許之事須合義，諾言才值得兌現" },
      { id: "b", text: "凡出口的話都須做到，不論內容", why: "這正是有子要修正的粗糙理解：不合義的諾言，兌現反而錯上加錯。" },
      { id: "c", text: "承諾要留有餘地，不可說得太滿", why: "「復」是實踐、兌現，不是留餘地。" },
      { id: "d", text: "守信的人自然會受到別人尊敬", why: "尊敬是後果不是條件，本句講的是諾言成立的前提。" },
    ],
    answerId: "a",
    explanation: "「復」是兌現。先問該不該諾，再問守不守諾——這是《論語》談信最要緊的一層，也是它與江湖義氣的分界。",
  },
  {
    id: "cm-1-16",
    type: "comprehension",
    refs: [16],
    concepts: ["zhiren"],
    difficulty: 1,
    cognitive: "understand",
    purpose: "把焦慮的方向從被理解調轉為理解他人",
    stem: "「不患人之不己知，患不知人也」，孔子要人把注意力放在哪裡？",
    options: [
      { id: "a", text: "與其怕人不懂我，不如怕我不懂人" },
      { id: "b", text: "既不必了解別人，也不必被人了解", why: "後半句明說「患不知人」，識人正是他要求的。" },
      { id: "c", text: "要努力讓更多人認識自己的才能", why: "這恰是被否定的那一頭。" },
      { id: "d", text: "別人不了解你是你德行還不夠", why: "本章不談原因，只調整用力的方向。" },
    ],
    answerId: "a",
    explanation: "全書多次重複這個調轉（14.30、15.19）。識人是可以練的能力，被人賞識不是——孔子把力氣放在可控的一邊。",
  },
  {
    id: "cm-2-3",
    type: "comprehension",
    refs: [19],
    concepts: ["zheng", "li"],
    difficulty: 2,
    cognitive: "analyze",
    purpose: "辨明刑罰只能止行為、不能生羞恥",
    stem: "「道之以政，齊之以刑，民免而無恥」——「免」指的是什麼？",
    context: "子曰：“道之以政，齐之以刑，民免而无耻；道之以德，齐之以礼，有耻且格。”",
    options: [
      { id: "a", text: "只求免於刑罰，卻不覺得可恥" },
      { id: "b", text: "百姓被免除了賦稅和勞役", why: "「免」承上句的「刑」，指躲開處罰，與賦役無關。" },
      { id: "c", text: "統治者赦免了百姓的過錯", why: "主語是民不是君；下句「有恥且格」也是就民而言。" },
      { id: "d", text: "百姓免於被統治，各自為政", why: "與全章講治理手段的對比毫不相干。" },
    ],
    answerId: "a",
    explanation: "孔子不是說刑政不能用，是說它只能買到「不被抓到」的服從。德禮要養出的是羞恥心——動力從外面的怕，換成裡面的恥。",
  },
  {
    id: "cm-2-4",
    type: "comprehension",
    refs: [20],
    concepts: ["xue", "tianming"],
    difficulty: 2,
    cognitive: "understand",
    purpose: "分清「立」與「不惑」各指什麼",
    stem: "「三十而立，四十而不惑」，這兩級各指什麼？",
    options: [
      { id: "a", text: "站得住腳跟；遇事不再疑惑動搖" },
      { id: "b", text: "成家立業；不再受人迷惑欺騙", why: "「立」指學與德的自立；「不惑」是自己心裡不疑，不是不被騙。" },
      { id: "c", text: "開始做官；不再更換自己的職務", why: "全章講為學的階段，不是仕途年表。" },
      { id: "d", text: "確立志向；不再感到迷惘徬徨", why: "志向在十五「志於學」已立，三十是志向已見成效而能自立。" },
    ],
    answerId: "a",
    explanation: "這是孔子唯一一次自述進境的全程：志學—立—不惑—知天命—耳順—從心所欲不踰矩，一路是從用力到不費力。",
  },
  {
    id: "cm-2-7",
    type: "comprehension",
    refs: [23],
    concepts: ["xiao"],
    difficulty: 1,
    cognitive: "understand",
    purpose: "看出孝的分界線在敬而不在供養",
    stem: "「今之孝者，是謂能養……不敬，何以別乎？」的要點是？",
    context: "子游问孝。子曰：“今之孝者，是谓能养。至于犬马，皆能有养；不敬，何以别乎？”",
    options: [
      { id: "a", text: "光能供養不算孝，分別在敬" },
      { id: "b", text: "養父母比養犬馬重要，不可顛倒", why: "章意不在排序，而在「養」之外還缺什麼。" },
      { id: "c", text: "犬馬也懂奉養父母，人更應如此", why: "楊伯峻取「人也能養犬馬」一路；無論哪種讀法，落點都在「不敬」。" },
      { id: "d", text: "孝要看物質條件，家貧難盡孝", why: "全章不談財力，只談心意。" },
    ],
    answerId: "a",
    explanation: "《為政》連問四次孝，答案各不相同。對子游說「敬」，對子夏說「色難」——都是把孝從「做到的事」推向「用的心」。",
  },
  {
    id: "cm-2-8",
    type: "comprehension",
    refs: [24],
    concepts: ["xiao"],
    difficulty: 2,
    cognitive: "understand",
    purpose: "理解「色難」難在心意藏不住",
    stem: "子夏問孝，孔子答「色難」。難的是什麼？",
    options: [
      { id: "a", text: "始終保持和悅的臉色與語氣" },
      { id: "b", text: "分辨父母臉色，隨時揣摩心意", why: "「色」指子女自己的神色。揣摩父母臉色是另一回事。" },
      { id: "c", text: "在外人面前替父母保住面子", why: "此章講家內日常，不涉場面。" },
      { id: "d", text: "為父母操辦體面的飲食與喪祭", why: "那正是下文說「曾是以為孝乎」所否定的表層作為。" },
    ],
    answerId: "a",
    explanation: "有事效勞、有酒食奉養，都是看得見的動作，做得到。臉色卻是心意的直接外現——所以孔子說最難的在這裡。",
  },
  {
    id: "cm-2-10",
    type: "comprehension",
    refs: [26],
    concepts: ["zhiren"],
    difficulty: 3,
    cognitive: "analyze",
    purpose: "看出識人三句是由外而內的遞進",
    stem: "「視其所以，觀其所由，察其所安」三句是並列還是遞進？",
    options: [
      { id: "a", text: "遞進：由所做，到所經，到所安" },
      { id: "b", text: "並列：三個角度，彼此沒有先後", why: "由「視」到「觀」到「察」，用力一層深過一層，末句才是根柢。" },
      { id: "c", text: "遞進：由動機，到手段，到結果", why: "次序正好相反——由外顯的行為推向內在的安處。" },
      { id: "d", text: "三句都指觀察對方的言論是否一致", why: "三句講的都是行事，不是言論。" },
    ],
    answerId: "a",
    explanation: "「所安」最難偽裝：一個人勉強做好事和樂於做好事，行為可以一樣，安不安卻藏不住。所以孔子連說兩遍「人焉廋哉」。",
  },
  {
    id: "cm-2-15",
    type: "comprehension",
    refs: [31],
    concepts: ["xue"],
    difficulty: 2,
    cognitive: "understand",
    purpose: "分清「罔」與「殆」是兩種不同的失",
    stem: "「學而不思則罔，思而不學則殆」，「罔」和「殆」各是什麼毛病？",
    options: [
      { id: "a", text: "受蒙蔽而無得；無依據而危殆" },
      { id: "b", text: "忘記了所學；懶怠而不肯用功", why: "「罔」非「忘」，「殆」非「怠」，兩處都是望字形而生的誤讀。" },
      { id: "c", text: "學得太雜亂；想得太多而無用", why: "毛病不在多寡，而在缺了另一半。" },
      { id: "d", text: "兩者程度不同，都指白費工夫", why: "殆比罔更重一層：不是沒收穫，是有危險。" },
    ],
    answerId: "a",
    explanation: "只接收不消化，等於被材料蒙住；只空想不取證，立論就沒有著落。兩句互為條件，不能只取一半當座右銘。",
  },
  {
    id: "cm-2-17",
    type: "comprehension",
    refs: [33],
    concepts: ["xue", "zhiren"],
    difficulty: 2,
    cognitive: "understand",
    purpose: "認出末字「知」當讀作「智」",
    stem: "「知之為知之，不知為不知，是知也」——最後一個「知」字怎麼講？",
    options: [
      { id: "a", text: "同「智」：肯認邊界才是聰明" },
      { id: "b", text: "仍是知道：這就是所謂的知道", why: "若末字仍作「知道」，全句成了同義反覆，也失了轉折。" },
      { id: "c", text: "指知識：這就是真正的知識", why: "本章講的是治學態度，不是知識內容。" },
      { id: "d", text: "指告知：這樣才好告訴別人", why: "沒有這種用法，也與孔子誨子路的語境不合。" },
    ],
    answerId: "a",
    explanation: "孔子是說給子路聽的——子路好逞強。承認邊界不是示弱，是智力誠實：知識的地圖必須標出空白處，才有用。",
  },
  {
    id: "cm-3-4",
    type: "comprehension",
    refs: [44],
    concepts: ["li"],
    difficulty: 3,
    cognitive: "analyze",
    purpose: "由「易」字的訓解看出禮的本在戚不在備",
    stem: "「喪，與其易也，寧戚」——「易」在此指什麼？",
    options: [
      { id: "a", text: "儀節辦得周到熟練，樣樣齊備" },
      { id: "b", text: "輕忽草率地把喪事辦過去", why: "若「易」是草率，取「戚」捨「易」便成廢話：兩者本來就不衝突。" },
      { id: "c", text: "更改舊有的喪禮制度與儀節", why: "全章對比的是同一件事的兩種做法，不是改制。" },
      { id: "d", text: "容易、省事，指一切從簡辦理", why: "從簡即是「儉」，孔子恰恰贊成；此處對舉的是另一組。" },
    ],
    answerId: "a",
    explanation: "把「易」讀成周到，這一問才成立：儀文完備和內心哀戚，若只能保一頭，孔子保後者。他稱林放這一問「大哉問」，正因為它問到了禮的根。",
  },
  {
    id: "cm-3-8",
    type: "comprehension",
    refs: [48],
    concepts: ["li", "wenzhi", "shijiao"],
    difficulty: 3,
    cognitive: "analyze",
    purpose: "理解「繪事後素」與「禮後」的類比關係",
    stem: "子夏由「繪事後素」悟出「禮後乎」，他悟到的是什麼？",
    context: "子夏问曰：“‘巧笑倩兮，美目盼兮，素以为绚兮。’何谓也？”子曰：“绘事后素。”曰：“礼后乎？”",
    options: [
      { id: "a", text: "先有素樸的底子，才談得上文飾" },
      { id: "b", text: "禮排在《詩》之後，應先學詩", why: "「後」說的是本末先後的邏輯，不是課程排序。" },
      { id: "c", text: "禮不重要，可以放到最後再說", why: "孔子若聽出這意思，不會贊他「始可與言詩已矣」。" },
      { id: "d", text: "作畫最後用白色勾邊以成其美", why: "類比的落點在「有底子才能加文」，不在技法細節。" },
    ],
    answerId: "a",
    explanation: "孔子最欣賞的正是這種舉一反三：從畫理跳到禮理。也因此他說「起予者商也」——學生的追問反過來啟發了老師。",
  },
  {
    id: "cm-3-17",
    type: "comprehension",
    refs: [57],
    concepts: ["li"],
    difficulty: 2,
    cognitive: "understand",
    purpose: "認出「愛」在先秦常訓作「吝惜」",
    stem: "「賜也！爾愛其羊，我愛其禮」的兩個「愛」字怎麼講？",
    options: [
      { id: "a", text: "可惜、捨不得，指吝惜其物" },
      { id: "b", text: "喜愛：你喜歡羊，我喜歡禮", why: "子貢要去掉餼羊是為省費，不是因為喜歡羊。" },
      { id: "c", text: "愛護：你護牲畜，我護制度", why: "「愛」在先秦常訓「吝惜」，此處正取這一義。" },
      { id: "d", text: "看重：你重實利，我重虛文", why: "方向雖近，但把孔子的立場說成「虛文」，正與他保存禮的用意相反。" },
    ],
    answerId: "a",
    explanation: "告朔之禮已廢，只剩一隻羊。孔子保下這隻羊，是保住制度的最後一點形跡——形跡若也去了，將來連恢復的線索都沒有。",
  },
  {
    id: "cm-4-5",
    type: "comprehension",
    refs: [71],
    concepts: ["yili"],
    difficulty: 2,
    cognitive: "analyze",
    purpose: "看清孔子限制的是取得方式而非富貴本身",
    stem: "「富與貴，是人之所欲也；不以其道得之，不處也。」孔子的立場是？",
    options: [
      { id: "a", text: "人皆欲之，惟取之不以道則不受" },
      { id: "b", text: "君子應當厭惡富貴，安於貧賤", why: "開頭就說「人之所欲也」，孔子並不否認這份願望。" },
      { id: "c", text: "富貴本身有害，終究會敗壞德行", why: "有害的是不以其道，而非富貴本身；7.12 還說富若可求，執鞭亦為之。" },
      { id: "d", text: "只要目的正當，手段就不必計較", why: "正好相反，本句限制的就是手段。" },
    ],
    answerId: "a",
    explanation: "這是《論語》義利觀的樞紐：判準不在你得到什麼，在你怎麼得到。下半章講貧賤「不以其道得之，不去也」，兩頭都用同一把尺。",
  },
  {
    id: "cm-4-10",
    type: "comprehension",
    refs: [76],
    concepts: ["yili", "junzi"],
    difficulty: 3,
    cognitive: "analyze",
    purpose: "分清「無適無莫」不是沒有立場",
    stem: "「君子之於天下也，無適也，無莫也，義之與比。」怎麼理解？",
    options: [
      { id: "a", text: "不預設可否，一切向義靠攏" },
      { id: "b", text: "對天下事不表態，保持中立", why: "末句「義之與比」正是最強的站隊，只是站在義那邊而非人那邊。" },
      { id: "c", text: "不親近誰也不疏遠誰，一視同仁", why: "這是把「適莫」讀成親疏；即便如此，落點仍在依義決定親疏。" },
      { id: "d", text: "凡事適可而止，不走任何極端", why: "那是中庸的講法，本章講的是判準來源。" },
    ],
    answerId: "a",
    explanation: "君子不是沒有原則，而是不把成見當原則。立場由每件事的義決定，所以看起來沒有固定立場，其實只有一個立場。",
  },
  {
    id: "cm-4-18",
    type: "comprehension",
    refs: [84],
    concepts: ["xiao"],
    difficulty: 2,
    cognitive: "analyze",
    purpose: "看出孝並不等於一味順從",
    stem: "「事父母幾諫，見志不從，又敬不違，勞而不怨。」這章對「順從」的態度是？",
    options: [
      { id: "a", text: "有錯要婉勸，勸不動仍敬不怨" },
      { id: "b", text: "父母的話一律照辦，不可異議", why: "「幾諫」明說要勸；一律照辦正是本章排除的。" },
      { id: "c", text: "勸不聽就該堅持到父母改變", why: "「見志不從，又敬不違」正是不硬頂。" },
      { id: "d", text: "勸諫無效便可以自行其是", why: "「不違」是不違逆，孝的分寸在勸而不逆。" },
    ],
    answerId: "a",
    explanation: "這一章是「愚孝」讀法的直接反證：該勸就勸，是責任；勸不動仍敬不怨，是分寸。孝在這兩者之間，不在任何一端。",
  },
  {
    id: "cm-4-19",
    type: "comprehension",
    refs: [85],
    concepts: ["xiao"],
    difficulty: 1,
    cognitive: "understand",
    purpose: "認出「方」指確定的去處",
    stem: "「父母在，不遠遊，遊必有方」的「方」指什麼？",
    options: [
      { id: "a", text: "確定的去處，讓父母尋得著" },
      { id: "b", text: "正當的理由，不可無故遠行", why: "「方」是方位、去向；理由是另一回事。" },
      { id: "c", text: "妥善的辦法，先安排好奉養", why: "安排奉養合乎情理，但不是「方」的字義。" },
      { id: "d", text: "一定的期限，說好何時回來", why: "期限也不是「方」；此字指的是所在。" },
    ],
    answerId: "a",
    explanation: "上半句常被引作不許出遠門，下半句其實已經鬆綁：可以遠行，條件是別讓父母牽掛得無處可尋。",
  },
  {
    id: "cm-5-10",
    type: "comprehension",
    refs: [102],
    concepts: ["zhiren", "xue"],
    difficulty: 2,
    cognitive: "analyze",
    purpose: "看出宰予一事引出的是識人方法的修正",
    stem: "孔子由宰予晝寢說「今吾於人也，聽其言而觀其行」，重點是？",
    options: [
      { id: "a", text: "他改了識人法：聽言之外還要觀行" },
      { id: "b", text: "他從此不再相信任何人說的話", why: "是加上觀行這一道，不是廢掉聽言。" },
      { id: "c", text: "他認為宰予的那套言論本身有錯", why: "被批評的是言行不副，不是言論內容。" },
      { id: "d", text: "他要求學生們少說話而多做事", why: "本句說的是孔子自己的判斷方式，主語是「吾於人也」。" },
    ],
    answerId: "a",
    explanation: "「於予與改是」——一個學生的失信，換來一條識人的通則。這也是《論語》裡少見的、孔子自陳修正的記錄。",
  },
  {
    id: "cm-5-15",
    type: "comprehension",
    refs: [107],
    concepts: ["xue"],
    difficulty: 2,
    cognitive: "understand",
    purpose: "認出諡「文」的依據在不恥下問",
    stem: "孔文子何以謂之「文」？孔子給的理由是？",
    options: [
      { id: "a", text: "敏而好學，且不恥於下問" },
      { id: "b", text: "文章寫得好，文采出眾動人", why: "「文」作為諡號評的是德行，不是文筆。" },
      { id: "c", text: "熟習典章制度，通曉古今文獻", why: "通曉文獻是結果，孔子點出的是「不恥下問」這一態度。" },
      { id: "d", text: "待人溫文有禮，進退皆有分寸", why: "溫文是另一路，本章明說「敏而好學，不恥下問」。" },
    ],
    answerId: "a",
    explanation: "子貢的疑問本身有意思：孔文子人品有虧，何以得美諡。孔子不替他整體洗白，只承認他確有的那一項——這是評人的分寸。",
  },
  {
    id: "cm-6-18",
    type: "comprehension",
    refs: [138],
    concepts: ["wenzhi", "junzi"],
    difficulty: 2,
    cognitive: "understand",
    purpose: "認出「史」與「野」同為偏失的一端",
    stem: "「質勝文則野，文勝質則史」——「史」在此是褒是貶？",
    options: [
      { id: "a", text: "貶：如史官之浮華而失於虛飾" },
      { id: "b", text: "褒：有歷史感、有文化的底蘊", why: "「史」與「野」對舉，同為偏失的一端，不可能一褒一貶。" },
      { id: "c", text: "中性：指記錄史實的客觀態度", why: "全句在說兩種偏失，中性讀法使對仗落空。" },
      { id: "d", text: "貶：拘泥於古書而不通世務", why: "方向對但取義不確，「史」偏在文勝，即虛飾浮誇。" },
    ],
    answerId: "a",
    explanation: "野是粗，史是浮，都不及格。「文質彬彬」是兩者相稱而非各半——這也是 12.8 子貢駁棘子成所守的同一條線。",
  },
  {
    id: "cm-6-20",
    type: "comprehension",
    refs: [140],
    concepts: ["xue"],
    difficulty: 1,
    cognitive: "understand",
    purpose: "分辨三層學習動機的由外而內",
    stem: "「知之者不如好之者，好之者不如樂之者」，三者的分別是？",
    options: [
      { id: "a", text: "懂它、喜歡它、以它為樂，層層由內" },
      { id: "b", text: "知道的人、愛好的人、快樂的人", why: "「之」指同一對象，說的是同一個人對它的三種關係。" },
      { id: "c", text: "學得快不如學得久，久不如輕鬆", why: "與快慢久暫無關，講的是動機的深淺。" },
      { id: "d", text: "知識不如興趣，興趣不如天賦", why: "第三層是「樂」不是天賦，全章不談稟賦。" },
    ],
    answerId: "a",
    explanation: "這是全書講學習動機最凝練的一章：外在的懂，到內在的好，到不覺其勞的樂。能走到第三層，努力就不再需要意志力支撐。",
  },
  {
    id: "cm-6-22",
    type: "comprehension",
    refs: [142],
    concepts: ["tianming", "zhiren"],
    difficulty: 3,
    cognitive: "analyze",
    purpose: "把握「敬而遠之」是敬而不依賴",
    stem: "「務民之義，敬鬼神而遠之，可謂知矣」——「遠之」是什麼態度？",
    options: [
      { id: "a", text: "保持距離不攀附，力氣放回人事" },
      { id: "b", text: "否認鬼神的存在，破除迷信", why: "前面還有一個「敬」字；孔子不否認，只是不靠。" },
      { id: "c", text: "疏遠祭祀，不必再行祭祀之禮", why: "孔子重祭如 3.12「祭如在」，遠的是求福攀附，不是禮。" },
      { id: "d", text: "敬畏鬼神，凡事先問卜再決定", why: "那正是「遠之」所排除的依賴。" },
    ],
    answerId: "a",
    explanation: "敬而不媚，這是孔子對超越者的一貫姿態：承認它、不利用它。所以子路請禱，他答「丘之禱久矣」——不另求，因為平日行事就是禱。",
  },
  {
    id: "cm-6-30",
    type: "comprehension",
    refs: [150],
    concepts: ["ren", "shu"],
    difficulty: 3,
    cognitive: "analyze",
    purpose: "認出「為仁之方」是可當下操作的方法",
    stem: "「能近取譬，可謂仁之方也已」——孔子給子貢的是什麼？",
    context: "子曰：“夫仁者，己欲立而立人，己欲达而达人。能近取譬，可谓仁之方也已。”",
    options: [
      { id: "a", text: "一個當下可用的方法：從身邊推起" },
      { id: "b", text: "一個崇高的目標：博施而能濟眾", why: "那正是子貢問的、孔子說「何事於仁，必也聖乎」而擱置的高標。" },
      { id: "c", text: "一句勉勵：仁很難，要慢慢來", why: "「方」是方法、門路，不是安慰。" },
      { id: "d", text: "一條戒律：不可以只顧自己", why: "本章給的是正面的推法，不是禁令。" },
    ],
    answerId: "a",
    explanation: "子貢把仁想成大規模的濟世，孔子把它拉回可執行的尺度：你想站住，就讓別人也站住。仁的入口一直在手邊。",
  },
  {
    id: "cm-7-1",
    type: "comprehension",
    refs: [151],
    concepts: ["xue", "li"],
    difficulty: 2,
    cognitive: "understand",
    purpose: "理解「述而不作」是自我定位而非保守",
    stem: "「述而不作，信而好古」，孔子的自我定位是？",
    options: [
      { id: "a", text: "傳述舊有而不自創制，信而好古" },
      { id: "b", text: "只轉述他人的話，自己不表意見", why: "他當然有主張；「作」指創制禮樂制度，不是不表意見。" },
      { id: "c", text: "只寫作而不創新，照抄古書成說", why: "「述」是傳承整理，「作」是制作；並非抄寫。" },
      { id: "d", text: "厚古薄今，反對一切制度變動", why: "他也說「殷因於夏禮，所損益可知也」，承認因革。" },
    ],
    answerId: "a",
    explanation: "這句自謙其實是立場：文化不是誰想造就能造的，它得有來歷。孔子把自己放在傳遞者的位置——而傳遞本身，正是他最大的創造。",
  },
  {
    id: "cm-7-8",
    type: "comprehension",
    refs: [158],
    concepts: ["jiaoxue", "xue"],
    difficulty: 3,
    cognitive: "analyze",
    purpose: "認出「憤悱」是啟發得以生效的時機",
    stem: "「不憤不啟，不悱不發」——「憤」與「悱」指學生的什麼狀態？",
    options: [
      { id: "a", text: "想通而未通、想說而說不清" },
      { id: "b", text: "憤怒與抱怨，鬧情緒時不宜教", why: "「憤」在此是求通而未得的心憤，與怒無關。" },
      { id: "c", text: "懶惰與敷衍，不用功的不必教", why: "孔子說「有教無類」；此章講的是時機，不是篩選。" },
      { id: "d", text: "已經完全弄懂並能清楚地表達", why: "若已通了，就無須啟發，全句便失去意思。" },
    ],
    answerId: "a",
    explanation: "教學的火候：講早了，學生接不住；講遲了，那口氣散了。「舉一隅不以三隅反，則不復也」——不肯自己推一步，再講也是白講。",
  },
  {
    id: "cm-7-16",
    type: "comprehension",
    refs: [166],
    concepts: ["yili", "junzi"],
    difficulty: 2,
    cognitive: "understand",
    purpose: "理解「如浮雲」形容的是與己無涉",
    stem: "「不義而富且貴，於我如浮雲」——「如浮雲」形容的是？",
    options: [
      { id: "a", text: "與我不相干，全然不放在心上" },
      { id: "b", text: "轉眼消散，不義之財留不長久", why: "重點不在它會不會消失，而在孔子對它的態度。" },
      { id: "c", text: "高不可攀，形容自己求它不得", why: "他明說可求則求（7.12），不是求不得。" },
      { id: "d", text: "輕飄虛浮，形容那種人沒分量", why: "喻體指向的是富貴，不是人。" },
    ],
    answerId: "a",
    explanation: "上半句「飯疏食飲水，曲肱而枕之，樂亦在其中矣」——真正的重點是「樂在其中」。不是咬牙拒絕誘惑，是根本不覺得那是誘惑。",
  },
  {
    id: "cm-8-7",
    type: "comprehension",
    refs: [195],
    concepts: ["ren", "junzi"],
    difficulty: 2,
    cognitive: "understand",
    purpose: "理清弘毅與任重道遠的對應關係",
    stem: "「士不可以不弘毅，任重而道遠」，「任重」與「道遠」分別由哪句解釋？",
    options: [
      { id: "a", text: "仁以為己任解任重，死而後已解道遠" },
      { id: "b", text: "仁以為己任解道遠，死而後已解任重", why: "次序顛倒了：擔子重是因為擔的是仁，路遠是因為要走到死。" },
      { id: "c", text: "兩句都在解釋前面的「弘毅」二字", why: "弘毅是前提，任重道遠是理由，下兩句分承後者。" },
      { id: "d", text: "兩句都在說做官任事的辛勞不易", why: "「士」在此指有志者，不專指官職。" },
    ],
    answerId: "a",
    explanation: "曾子把仁講成一件終身的差事：不是偶爾行善，是接下來一輩子的工作內容。所以要「弘」（心胸夠寬）也要「毅」（撐得夠久）。",
  },
  {
    id: "cm-9-4",
    type: "comprehension",
    refs: [213],
    concepts: ["junzi", "zhiren"],
    difficulty: 3,
    cognitive: "analyze",
    purpose: "準確解出毋意毋必毋固毋我四項",
    stem: "「子絕四——毋意，毋必，毋固，毋我」，四者依次指？",
    options: [
      { id: "a", text: "不臆測、不絕對、不固執、不自我" },
      { id: "b", text: "沒有意見、沒必要、不固執、無我", why: "「毋意」是不臆測，不是沒有意見；孔子意見很多。" },
      { id: "c", text: "不重意圖、不講必然、不守成規", why: "「毋我」不是不談自己，是不以自我為中心。" },
      { id: "d", text: "不隨意、不勉強、不僵化、不自私", why: "接近但不確：四字有明確的認識論所指，尤其「意」是臆測。" },
    ],
    answerId: "a",
    explanation: "這四條合起來是一套思維紀律：對證據留餘地、對結論留餘地、對自己留餘地。它解釋了為什麼孔子答同一問題會有不同答案。",
  },
  {
    id: "cm-9-17",
    type: "comprehension",
    refs: [226],
    concepts: ["xue", "tianming"],
    difficulty: 2,
    cognitive: "analyze",
    purpose: "看出川上之嘆是策勵而非傷感",
    stem: "「逝者如斯夫！不舍晝夜」——孔子在川上感嘆的重點是？",
    options: [
      { id: "a", text: "萬化不息，人亦當不間斷地用功" },
      { id: "b", text: "感傷人生短暫，功業終究難成", why: "「不舍晝夜」是稱許水的不間斷，語氣是策勵不是哀嘆。" },
      { id: "c", text: "說明世事無常，不必過於執著", why: "那是後世佛老的讀法，與孔子勉學的語境不合。" },
      { id: "d", text: "以水比喻智者，呼應知者樂水", why: "6.23 是另一章的比喻，此處喻的是不息。" },
    ],
    answerId: "a",
    explanation: "配合 9.19「譬如為山，未成一簣，止，吾止也」讀：孔子講進度，向來把主動權放在自己身上——停是自己停，進也是自己進。",
  },
  {
    id: "cm-9-29",
    type: "comprehension",
    refs: [238, 404],
    concepts: ["ren", "zhiren", "junzi"],
    difficulty: 2,
    cognitive: "understand",
    purpose: "理解三達德各對應一類問題",
    stem: "「知者不惑，仁者不憂，勇者不懼」——為什麼是「仁者不憂」？",
    options: [
      { id: "a", text: "心不繫於一己得失，故無所憂" },
      { id: "b", text: "仁者知識豐富，凡事都想得明白", why: "想得明白是「知者不惑」那一項。" },
      { id: "c", text: "仁者性情溫和，天生不容易焦慮", why: "講的是德行的效果，不是性格氣質。" },
      { id: "d", text: "仁者人緣好，遇事自然有人幫忙", why: "不憂的根據在自身用心，不在外援。" },
    ],
    answerId: "a",
    explanation: "三句各有所對：惑是認知問題，憂是價值問題，懼是意志問題。14.28 孔子把這三句反過來說成「我無能焉」，子貢說那是夫子自道。",
  },
  {
    id: "cm-11-12",
    type: "comprehension",
    refs: [282],
    concepts: ["tianming", "li"],
    difficulty: 3,
    cognitive: "analyze",
    purpose: "看出「未知生焉知死」是定次序而非迴避",
    stem: "季路問事鬼神與死，孔子答「未能事人，焉能事鬼」。這是？",
    options: [
      { id: "a", text: "定次序：先明人事與生，才及鬼神" },
      { id: "b", text: "拒絕回答，表示這類問題不可談", why: "他給的是次序而非禁令；若不可談，就不會有「焉知死」的邏輯連詞。" },
      { id: "c", text: "否認鬼神與死後世界確實存在", why: "孔子未作此論斷，6.22 還說「敬鬼神」。" },
      { id: "d", text: "責備子路把問題問得太過淺陋", why: "問題並不淺，孔子也未責備，只是排了先後。" },
    ],
    answerId: "a",
    explanation: "這不是迴避而是方法：離開對生的理解去談死，說什麼都無從檢驗。孔子把形上問題釘回可以下手的地方——人事。",
  },
  {
    id: "cm-12-1",
    type: "comprehension",
    refs: [323],
    concepts: ["ren", "li"],
    difficulty: 3,
    cognitive: "analyze",
    purpose: "把握「為仁由己」確立的主體性",
    stem: "「為仁由己，而由人乎哉？」補在「克己復禮」之後，用意是？",
    options: [
      { id: "a", text: "仁的動力在己，不待外人給條件" },
      { id: "b", text: "說明行仁不必顧及他人的感受", why: "仁正是待人之道，怎會不顧他人；「由己」講的是動力來源。" },
      { id: "c", text: "說明只要自己覺得對就算是仁", why: "上句已用「復禮」立了客觀標準，不是憑感覺。" },
      { id: "d", text: "說明仁出於天生，後天學不來", why: "「我欲仁，斯仁至矣」正說明它可欲可求。" },
    ],
    answerId: "a",
    explanation: "克己復禮給的是內容，為仁由己給的是動力。前者防止仁流於主觀，後者防止仁被推給環境——顏淵得的是全副的答案。",
  },
  {
    id: "cm-12-7",
    type: "comprehension",
    refs: [329],
    concepts: ["xin", "zheng"],
    difficulty: 3,
    cognitive: "evaluate",
    purpose: "記住去食去兵而不可去信的次序",
    stem: "「足食，足兵，民信之矣」，被迫去取時的次序是？",
    options: [
      { id: "a", text: "先去兵，再去食，信終不可去" },
      { id: "b", text: "先去信，再去兵，食最不能去", why: "正相反：孔子寧可餓，也不肯讓民不信。" },
      { id: "c", text: "先去食，再去信，兵最不能去", why: "兵是最先捨的一項。" },
      { id: "d", text: "三者同等重要，無法排出次序", why: "孔子明確排了序，這正是本章的分量所在。" },
    ],
    answerId: "a",
    explanation: "「自古皆有死，民無信不立」——他把信排在生存之上。不是輕看飢餓，是判斷：失去糧食是災難，失去互信則政權根本不成立。",
  },
  {
    id: "cm-12-22",
    type: "comprehension",
    refs: [344],
    concepts: ["ren", "zhiren"],
    difficulty: 2,
    cognitive: "understand",
    purpose: "看出「舉直錯諸枉」是在解「知人」",
    stem: "樊遲問仁問知得「愛人」「知人」，又補「舉直錯諸枉」。補的是哪一項？",
    options: [
      { id: "a", text: "解「知人」：識人須落到用人上" },
      { id: "b", text: "解「愛人」：提拔正直者即是愛人", why: "子夏舉舜舉皋陶、湯舉伊尹為例，說的都是識人任人。" },
      { id: "c", text: "兩者都不解，是另起的政事話題", why: "子夏明說「富哉言乎」並就此發揮，正是承接前問。" },
      { id: "d", text: "解仁知合一，兩者本來不可分說", why: "本章恰恰是分說兩問，各給一答。" },
    ],
    answerId: "a",
    explanation: "樊遲一時沒懂，去問子夏——這段記錄難得地保留了學生課後補課的過程，也讓「知人」從兩個字變成一套用人的方法。",
  },
  {
    id: "cm-13-18",
    type: "comprehension",
    refs: [364],
    concepts: ["xiao", "yili"],
    difficulty: 3,
    cognitive: "evaluate",
    purpose: "理解孔子把「直」安放在人倫真情之中",
    stem: "葉公以子證父攘羊為直，孔子說「父為子隱，子為父隱，直在其中矣」。意思是？",
    options: [
      { id: "a", text: "直不在揭發至親，而在真情不偽" },
      { id: "b", text: "包庇親人的罪行應該，法律不顧", why: "孔子談的是「直」的判準，未主張廢法；他也未說父子當共謀。" },
      { id: "c", text: "兒子應先勸父自首，再議舉發", why: "章中無此層次，這是後人調和的講法。" },
      { id: "d", text: "葉公說得對，孔子只是委婉反對", why: "「吾黨之直者異於是」是明確的不同意。" },
    ],
    answerId: "a",
    explanation: "這是全書最尖銳的一章，也最容易被兩邊各取一半。孔子質疑的是：一種要人違背至親之情才能成立的「正直」，究竟是不是直。",
  },
  {
    id: "cm-13-23",
    type: "comprehension",
    refs: [369],
    concepts: ["zhongyong", "junzi"],
    difficulty: 2,
    cognitive: "analyze",
    purpose: "分清「和」是存異而「同」是滅異",
    stem: "「君子和而不同，小人同而不和」——「和」與「同」差在哪裡？",
    options: [
      { id: "a", text: "和是存異而能協調，同是滅異而不協" },
      { id: "b", text: "和是意見一致，同是表面上附和", why: "「和」恰恰不要求意見一致，這是本章最關鍵的一點。" },
      { id: "c", text: "和是與人為善，同是與人同流合污", why: "方向對，但把差別放在品行上，漏掉了「差異能否並存」這個核心。" },
      { id: "d", text: "和指和氣待人，同指立場完全相同", why: "和不是和氣，是不同成分之間的相成。" },
    ],
    answerId: "a",
    explanation: "「和」本是烹調與音樂的詞：不同的味與聲配得起來才叫和。全體一致反而是「同」——沒有差異可調，也就沒有和可言。",
  },
  {
    id: "cm-14-34",
    type: "comprehension",
    refs: [410],
    concepts: ["ren", "yili"],
    difficulty: 3,
    cognitive: "evaluate",
    purpose: "分清「以直報怨」既非報復也非以德報怨",
    stem: "「以直報怨，以德報德」——「以直報怨」是什麼意思？",
    options: [
      { id: "a", text: "按事情本身的是非公正回應" },
      { id: "b", text: "以牙還牙，怨恨要如數報復回去", why: "「直」是公正不是報復；若主張報復，就不會保留「以德報德」的區別。" },
      { id: "c", text: "坦白告訴對方自己心中的不滿", why: "「直」在此指處置的公正，不是直言。" },
      { id: "d", text: "同意以德報怨，只是換個說法而已", why: "他明白反問「何以報德」，正是指出以德報怨會讓德無處安放。" },
    ],
    answerId: "a",
    explanation: "孔子的反問很鋒利：若對仇人也用恩德，那對恩人拿什麼？區別對待不是小氣，是讓善意保有意義。",
  },
  {
    id: "cm-15-24",
    type: "comprehension",
    refs: [444],
    concepts: ["shu", "ren"],
    difficulty: 2,
    cognitive: "understand",
    purpose: "分辨恕的否定式表述所設的是底線",
    stem: "「己所不欲，勿施於人」用否定句表述，與肯定式的「己欲立而立人」相比，特點是？",
    options: [
      { id: "a", text: "劃出人人做得到的底線" },
      { id: "b", text: "兩句意思完全相同，只是句式不同", why: "一個是不做什麼，一個是主動做什麼，要求的高度不同。" },
      { id: "c", text: "否定式較為消極，不如肯定式可取", why: "孔子答子貢「終身行之」的正是這一句，可見他重視它的可行性。" },
      { id: "d", text: "否定式針對小人，肯定式針對君子", why: "章中並無這樣的對象分別，兩句都是對一般人說的。" },
    ],
    answerId: "a",
    explanation: "子貢問的是「一言而可以終身行之」——要一條隨時能用的。孔子給的是底線版：能守住不強加於人，就已經在行仁的路上。",
  },
  {
    id: "cm-15-29",
    type: "comprehension",
    refs: [449],
    concepts: ["ren", "xue"],
    difficulty: 3,
    cognitive: "analyze",
    purpose: "看清道須人踐行才得以光大",
    stem: "「人能弘道，非道弘人」的意思是？",
    options: [
      { id: "a", text: "道靠人去做才光大，不會反過來抬人" },
      { id: "b", text: "人的能力比道本身更重要也更根本", why: "不是比大小，是說道的實現依賴人的踐行。" },
      { id: "c", text: "只要學了道，人自然就會有所成就", why: "這正是被否定的那一半「道弘人」。" },
      { id: "d", text: "道無所不在，因此不必刻意去追求", why: "與「能弘」的積極義相反。" },
    ],
    answerId: "a",
    explanation: "這句是對一切「我站在正確的一邊所以我正確」的預先反駁：真理不會因為你引用它而移轉到你身上，只有做出來才算數。",
  },
  {
    id: "cm-15-31",
    type: "comprehension",
    refs: [451],
    concepts: ["xue"],
    difficulty: 2,
    cognitive: "understand",
    purpose: "看出這是「思而不學則殆」的親身驗證",
    stem: "「終日不食，終夜不寢，以思，無益，不如學也」與 2.15 的關係是？",
    options: [
      { id: "a", text: "同一主張的現身說法：空想沒有產出" },
      { id: "b", text: "推翻了 2.15，改主張只要學不必思", why: "「無益」針對的是脫離學的空思，並未取消思。" },
      { id: "c", text: "說明身體要緊，不可以廢寢忘食", why: "廢寢忘食只是敘述背景，結論落在思與學。" },
      { id: "d", text: "自述年輕時走過彎路，後來才知學", why: "這是舉例論證，不是自述生平轉折。" },
    ],
    answerId: "a",
    explanation: "他用自己試過的極端案例作證：一天一夜不吃不睡地想，抵不上老實去學。這比抽象講「學思並重」更有說服力。",
  },
  {
    id: "cm-16-9",
    type: "comprehension",
    refs: [471],
    concepts: ["xue", "jiaoxue"],
    difficulty: 2,
    cognitive: "analyze",
    purpose: "找出四等中真正由自己決定的分界",
    stem: "「生而知之／學而知之／困而學之／困而不學」四等，真正的分界線在哪裡？",
    options: [
      { id: "a", text: "在三與四之間：遇困肯不肯學" },
      { id: "b", text: "在一與二之間：天賦決定了上限", why: "孔子自承「我非生而知之者」，卻不以此自貶——可見他不把天賦當分界。" },
      { id: "c", text: "在二與三之間：主動學勝過被動學", why: "主動被動有別，但「困而學之」仍在可取之列，最下的是不學。" },
      { id: "d", text: "四等全由稟賦決定，無所謂分界", why: "末等「民斯為下矣」是評價選擇，不是評價稟賦。" },
    ],
    answerId: "a",
    explanation: "前三等的差別是起點，最後一等的差別是選擇。孔子把自己歸在第二等（「好古，敏以求之者也」），正是要說明起點不是命運。",
  },
  {
    id: "cm-17-2",
    type: "comprehension",
    refs: [478],
    concepts: ["xue", "jiaoxue"],
    difficulty: 2,
    cognitive: "analyze",
    purpose: "看出「習相遠」是教育有效的根據",
    stem: "「性相近也，習相遠也」為孔子的哪一主張提供了根據？",
    options: [
      { id: "a", text: "有教無類：差距由後天造成" },
      { id: "b", text: "唯上知與下愚不移：天賦決定一切", why: "那是下一章，恰是本章的例外而非推論。" },
      { id: "c", text: "因材施教：每個人的天性各不相同", why: "本章說的正是天性「相近」，差異來自習。" },
      { id: "d", text: "述而不作：古人的天性勝過今人", why: "章中無古今優劣之意。" },
    ],
    answerId: "a",
    explanation: "六個字撐起整套教育觀：起點差不多，差別是後天拉開的——所以教育有用，所以「有教無類」不是口號而是推論。",
  },
  {
    id: "cm-17-9",
    type: "comprehension",
    refs: [485],
    concepts: ["shijiao"],
    difficulty: 2,
    cognitive: "understand",
    purpose: "理解「怨」是有分寸的公共表達",
    stem: "「詩可以興，可以觀，可以群，可以怨」——「怨」指的是？",
    options: [
      { id: "a", text: "抒發不平、諷諫，使怨有出口" },
      { id: "b", text: "抱怨他人，藉以發洩私人的情緒", why: "《詩》的怨是公共的、有分寸的表達，與私怨不同。" },
      { id: "c", text: "怨恨命運不公，感嘆生不逢時", why: "孔子明言「不怨天，不尤人」，此處的怨不指向天命。" },
      { id: "d", text: "指《詩》中充滿哀怨的那類作品", why: "四項說的是讀詩的功能，不是題材分類。" },
    ],
    answerId: "a",
    explanation: "興是感發、觀是察世、群是合眾、怨是表達不滿——四項合起來，《詩》是一套完整的表達與感受訓練，不是背誦材料。",
  },
  {
    id: "cm-19-6",
    type: "comprehension",
    refs: [519],
    concepts: ["xue", "ren"],
    difficulty: 2,
    cognitive: "analyze",
    purpose: "認出「近思」是就切身處著想",
    stem: "「博學而篤志，切問而近思，仁在其中矣」，「近思」的「近」是？",
    options: [
      { id: "a", text: "從切身處著想，不騖高遠" },
      { id: "b", text: "思考得淺近簡單，不必深入細究", why: "「近」指切近自身，不指思考深度。" },
      { id: "c", text: "順著老師的思路去想，不另立說", why: "無此意；「切問」已含向人請教，「近思」轉回自身。" },
      { id: "d", text: "最近才開始思考的那些新問題", why: "「近」在此非時間義。" },
    ],
    answerId: "a",
    explanation: "四件事都在手邊：多學、守志、切實地問、就近地想。子夏說仁就在這裡面——不是另有一件叫「行仁」的大事要辦。",
  },
  {
    id: "cm-20-3",
    type: "comprehension",
    refs: [541],
    concepts: ["tianming", "li", "zhiren"],
    difficulty: 3,
    cognitive: "analyze",
    purpose: "把握全書末章三句各對一個面向",
    stem: "「不知命，無以為君子；不知禮，無以立；不知言，無以知人」，三句依次對應？",
    options: [
      { id: "a", text: "安身、立身、識人三個面向" },
      { id: "b", text: "天命、禮節、言辭三種必備知識", why: "三句不是並列的知識清單，各有所對的面向。" },
      { id: "c", text: "算命、守禮、辯論三種實用本事", why: "「知命」不是占卜，「知言」不是辯論。" },
      { id: "d", text: "由淺入深排列的三個學習階段", why: "三句是三個面向而非階段，末句也不比首句深。" },
    ],
    answerId: "a",
    explanation: "編者把這三句放在全書最後：知命所以不妄求，知禮所以站得住，知言所以看得懂人。三面合起來，正是《論語》對「君子」的完整交代。",
  },
];
